import React, { useEffect, useState, useCallback } from 'react';
import { Cookie, Shield, BarChart3, Target, X } from 'lucide-react';

const STORAGE_KEY = 'geyser_cookie_consent_v1';
const PRIVACY_URL = 'https://www.stockergarden.com/privacy-policy/';
const GA_ID = 'G-4JXPDWVPFJ';
const META_PIXEL_ID = '3837962179578957';

// ---- Script loaders (idempotent) ----
function loadGoogleAnalytics() {
  if (window.__gaLoaded) return;
  window.__gaLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { anonymize_ip: true });
}

function loadMetaPixel() {
  if (window.__fbqLoaded) return;
  window.__fbqLoaded = true;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s);}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
}

function applyConsent(consent) {
  if (consent?.analytics) loadGoogleAnalytics();
  if (consent?.marketing) loadMetaPixel();
}

// Exposed so other components (e.g. admin) can reset consent on logout or privacy clicks
export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent('geyser:open-cookie-prefs'));
}

const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  // Initial check + auto-apply stored consent
  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setPrefs({ necessary: true, analytics: !!stored.analytics, marketing: !!stored.marketing });
      applyConsent(stored);
    } else {
      setOpen(true);
    }
  }, []);

  // Listen for manual re-open from footer link
  useEffect(() => {
    const handler = () => {
      const stored = getStoredConsent();
      if (stored) setPrefs({ necessary: true, analytics: !!stored.analytics, marketing: !!stored.marketing });
      setShowPrefs(true);
      setOpen(true);
    };
    window.addEventListener('geyser:open-cookie-prefs', handler);
    return () => window.removeEventListener('geyser:open-cookie-prefs', handler);
  }, []);

  const persist = useCallback((consent) => {
    const payload = { ...consent, ts: new Date().toISOString(), v: 1 };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (_) { /* ignore */ }
    applyConsent(payload);
    setOpen(false);
    setShowPrefs(false);
  }, []);

  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, analytics: false, marketing: false });
  const saveSelection = () => persist(prefs);

  if (!open) return null;

  // Don't show consent on admin pages (internal tool, not public-facing)
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin6y5dv2x')) {
    return null;
  }

  return (
    <div className="fixed bottom-0 inset-x-0 md:bottom-6 md:left-1/2 md:-translate-x-1/2 md:max-w-3xl w-full z-[9999] px-0 md:px-4 pointer-events-auto"
      role="dialog"
      aria-label="Consenso cookie"
      data-testid="cookie-consent-banner"
    >
        <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {!showPrefs ? (
            <div className="p-5 md:p-6">
              <div className="flex items-start gap-3 mb-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-stocker-green/10 flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-stocker-green" />
                </div>
                <div className="flex-1">
                  <h2 className="font-bold text-gray-900 text-lg leading-tight">Utilizziamo i cookie</h2>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                    Utilizziamo cookie tecnici necessari al funzionamento del sito e, con il tuo consenso, cookie di analisi e marketing
                    per migliorare la tua esperienza. Puoi accettare tutti i cookie, rifiutarli o personalizzare le tue preferenze.{' '}
                    <a
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-stocker-green hover:underline font-medium"
                      data-testid="cookie-privacy-link"
                    >
                      Leggi la Privacy Policy
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                <button
                  onClick={rejectAll}
                  className="flex-1 sm:flex-initial px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition"
                  data-testid="cookie-reject-btn"
                >
                  Rifiuta tutti
                </button>
                <button
                  onClick={() => setShowPrefs(true)}
                  className="flex-1 sm:flex-initial px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition"
                  data-testid="cookie-customize-btn"
                >
                  Personalizza
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 sm:flex-initial sm:ml-auto px-4 py-2.5 rounded-lg bg-stocker-green hover:bg-stocker-green-dark text-white text-sm font-semibold transition shadow-sm"
                  data-testid="cookie-accept-all-btn"
                >
                  Accetta tutti
                </button>
              </div>
            </div>
          ) : (
            <div className="p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-gray-900 text-lg">Preferenze cookie</h2>
                <button onClick={() => setShowPrefs(false)} className="p-1 rounded hover:bg-gray-100" data-testid="cookie-close-prefs-btn">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                <CategoryRow
                  icon={Shield}
                  title="Necessari"
                  description="Questi cookie sono indispensabili per il funzionamento del sito e non possono essere disattivati."
                  locked
                  checked={true}
                />
                <CategoryRow
                  icon={BarChart3}
                  title="Analitici"
                  description="Ci aiutano a capire come viene utilizzato il sito (Google Analytics) in forma anonima, per migliorare l'esperienza."
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs(p => ({ ...p, analytics: v }))}
                  testId="cookie-toggle-analytics"
                />
                <CategoryRow
                  icon={Target}
                  title="Marketing"
                  description="Utilizzati per misurare l'efficacia delle campagne pubblicitarie (Meta Pixel) e mostrarti contenuti pertinenti."
                  checked={prefs.marketing}
                  onChange={(v) => setPrefs(p => ({ ...p, marketing: v }))}
                  testId="cookie-toggle-marketing"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-5 pt-4 border-t border-gray-100">
                <a
                  href={PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-stocker-green hover:underline self-center sm:mr-auto"
                >
                  Privacy Policy
                </a>
                <button
                  onClick={rejectAll}
                  className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition"
                  data-testid="cookie-prefs-reject-btn"
                >
                  Rifiuta tutti
                </button>
                <button
                  onClick={saveSelection}
                  className="px-4 py-2.5 rounded-lg bg-stocker-green hover:bg-stocker-green-dark text-white text-sm font-semibold transition shadow-sm"
                  data-testid="cookie-prefs-save-btn"
                >
                  Salva preferenze
                </button>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

const CategoryRow = ({ icon: Icon, title, description, checked, onChange, locked, testId }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50/50">
    <Icon className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
    <div className="flex-1">
      <div className="flex items-center justify-between gap-3 mb-1">
        <span className="font-semibold text-gray-900 text-sm">{title}</span>
        {locked ? (
          <span className="text-xs text-gray-500 font-medium">Sempre attivi</span>
        ) : (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
              className="sr-only peer"
              data-testid={testId}
            />
            <div className="w-10 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-stocker-green transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-4"></div>
          </label>
        )}
      </div>
      <p className="text-xs text-gray-600 leading-relaxed">{description}</p>
    </div>
  </div>
);

export default CookieConsent;
